import { Team } from '@shared/models/team.model'
import { Vehicle } from '@shared/models/vehicle.model'
import { Depot } from '@shared/models/depot.model'
import { VehicleGroup } from '@shared/models/vehicle-group.model'
import { UserGroup } from '@shared/models/user-group.model'
import { Geofence, GeofenceGroup } from '@app/modules/client/shared/models/geofence.model'
import { Device } from '@shared/models/device.model'
import { User } from '@shared/models/user.model'
import { Role } from '@shared/models/role.model'
import { EventHeadersColumns } from '../../alerts/shared/event-log.model'
import { Asset } from '@shared/models/asset.model'
import { Sensor } from '@shared/models/sensor.model'
import { Driver } from '@shared/models/driver.model'

export class Notification {
  id?: number
  title: string
  status: NotificationStatus
  importance: Importance
  sendTimeFrom?: string
  sendTimeUntil?: string
  eventId: number
  listenerTeam?: Team
  scope: NotificationScope
  additionalScope: NotificationScope
  recipients: NotificationRecipients[]
  transports: TransportType[]
  createdAt?: string
  eventTrackingDays: Day[]
  eventTrackingTimeFrom: string
  eventTrackingTimeUntil: string
  acknowledgeRecipients: NotificationRecipients[]
  teamId: number
  comment: string
  additionalParams?: NotificationAdditionalParams
}

export type NotificationFormData = Partial<Notification> &
  Partial<NotificationAdditionalParamsFormData> & {
    acknowledgeRecipientsValue: NotificationScopeItem[]
    scope: EventScope
    disabled: boolean
    acknowledgeRecipientsType: RecipientType
    scopeSubType: NotificationScopeItem[]
    additionalScopeSubType: NotificationScopeItem[]
    recipientsValue: NotificationScopeItem[]
    recipientsType: RecipientType
  }

export interface NotificationAdditionalParamsFormData {
  deviceVoltage?: number
  overSpeed?: number
  distance?: number
  timeDuration?: number
  sensorLight?: boolean
  batteryLevel?: number
  sensorStatus?: boolean
  temperature?: { from: number; to: number; type: SensorOperator }
  humidity?: { from: number; to: number; type: SensorOperator }
  statusIO?: number
  sensorIOTypeId?: string
  deviceBatteryPercentage?: number
  areaTriggerType?: boolean
  exprOperator?: ExpressionOperatorEnum | string
  thresholdSpeedLimit?: number
  ttsText?: string
}

export interface NotificationAdditionalParams {
  distance?: number
  overSpeed?: number
  timeDuration?: number
  deviceVoltage?: number
  deviceBatteryPercentage?: number
  status?: boolean
  light?: boolean
  batteryLevel?: number
  from?: number
  type?: SensorOperator
  to?: number
  temperature?: number
  humidity?: number
  statusIO?: number
  sensorIOTypeId?: string
  areaTriggerType?: boolean
  exprOperator: ExpressionOperatorEnum | string
  thresholdSpeedLimit?: number
  ttsText?: string
}

export enum ScopeSubType {
  Any = 'any',
  Vehicle = 'vehicle',
  Depot = 'depot',
  Group = 'group',
  Role = 'role',
  Device = 'device',
  Team = 'team',
  TeamType = 'team_type',
  User = 'user',
  UserGroup = 'user_groups',
  Asset = 'asset',
  Sensor = 'sensor',
  Driver = 'driver',
  Document = 'document',
  Reminder = 'reminder',
  All = 'all',
}

export enum AdditionalScopeSubType {
  Any = 'any',
  All = 'all',
  Area = 'area',
  AreaGroup = 'area_group',
}

export interface NotificationScope {
  subtype: ScopeSubType
  subType?: ScopeSubType
  value: number[] | string[]
}

export interface EventScope {
  name: string
  subType: ScopeSubType
}

export interface AdditionalEventScope {
  name: string
  subType: AdditionalScopeSubType
}

export interface EventType {
  id: number
  name: string
  alias: string
  eventSource: ScopeSubType
  additionalSettings: EventAdditionalSetting | null
  scopes: EventScope[]
  additionalScopes: AdditionalEventScope[]
}

export interface EventAdditionalSetting {
  isDistance: boolean
  isTimeDuration?: boolean
  isOverSpeed?: boolean
  isDeviceVoltage?: boolean
  isDeviceBatteryPercentage?: boolean
  isSensorTemperature?: boolean
  isSensorHumidity?: boolean
  isSensorLight?: boolean
  isSensorBatteryLevel?: boolean
  isSensorStatus?: boolean
  isSensorIOStatus?: boolean
  isSensorIOType?: boolean
  isAreaTriggerType?: boolean
  headersColumns: EventHeadersColumns
  isExpressionOperator?: boolean
  isThresholdSpeedLimit?: boolean
  isCameras?: boolean
  isTts?: boolean
}

export enum NotificationStatus {
  Enabled = 'enabled',
  Disabled = 'disabled',
}

export const NotificationStatusLabel = {
  [NotificationStatus.Enabled]: 'FIELDS.ENABLED',
  [NotificationStatus.Disabled]: 'FIELDS.DISABLED',
} as const

export enum RecipientType {
  Any = 'any',
  UserList = 'users_list',
  Role = 'role',
  UserGroupList = 'user_groups_list',
  OtherEmail = 'other_email',
  OtherPhone = 'other_phone',
}

export interface NotificationRecipients {
  type: RecipientType
  value: (string | number)[]
}

export enum Importance {
  Immediately = 'immediately',
  BusinessHours = 'business_hours',
  Custom = 'custom',
}

export enum TransportType {
  Sms = 'sms',
  Email = 'email',
  InApp = 'inApp',
}

export interface Transport {
  name: string
  alias: TransportType
  active: boolean
}

export interface NotificationTemplate {
  id: number
  name: string
}

export enum Day {
  Monday = 'monday',
  Tuesday = 'tuesday',
  Wednesday = 'wednesday',
  Thursday = 'thursday',
  Friday = 'friday',
  Saturday = 'saturday',
  Sunday = 'sunday',
}

export interface NotificationScopeItem {
  id: number | string
  label: NotificationScopeItemLabel | NotificationAdditionalScopeItemLabel | NotificationRecipientScopeItemLabel
  team: Team
}

export type NotificationScopeItemLabel =
  | Extract<keyof Vehicle, 'regNo'>
  | Extract<keyof Depot | keyof VehicleGroup | keyof UserGroup | keyof Asset, 'name'>
  | Extract<keyof Device, 'imei'>
  | Extract<keyof User, 'fullName'>
  | Extract<keyof Role, 'displayName'>
  | Extract<keyof Sensor, 'label'>
  | Extract<keyof Driver, 'fullName'>

export type NotificationAdditionalScopeItemLabel = Extract<keyof Geofence | keyof GeofenceGroup, 'name'>

export type NotificationRecipientScopeItemLabel =
  | Extract<keyof UserGroup, 'name'>
  | Extract<keyof User, 'fullName'>
  | Extract<keyof Role, 'displayName'>

export enum SensorLight {
  Off = 0,
  On,
}

export const SensorLightLabels: Record<SensorLight, string> = {
  [SensorLight.On]: 'NOTIFICATIONS.SENSOR_LIGHT.ON',
  [SensorLight.Off]: 'NOTIFICATIONS.SENSOR_LIGHT.OFF',
} as const

export enum SensorStatus {
  Offline = 0,
  Online,
}

export const SensorStatusLabels: Record<SensorStatus, string> = {
  [SensorStatus.Online]: 'NOTIFICATIONS.SENSOR_STATUS.ONLINE',
  [SensorStatus.Offline]: 'NOTIFICATIONS.SENSOR_STATUS.OFFLINE',
} as const

export enum SensorOperator {
  Less = 'less',
  Greater = 'greater',
  Outside = 'outside',
}

export const SensorOperatorLabels: Record<SensorOperator, string> = {
  [SensorOperator.Less]: 'NOTIFICATIONS.SENSOR_OPERATOR.LESS',
  [SensorOperator.Greater]: 'NOTIFICATIONS.SENSOR_OPERATOR.MORE',
  [SensorOperator.Outside]: 'NOTIFICATIONS.SENSOR_OPERATOR.OUTSIDE',
} as const

export enum NotificationAreaTriggerTypeEnum {
  Everywhere = 'everywhere',
  Inside = 'inside',
  Outside = 'outside',
}

export enum ExpressionOperatorEnum {
  And = 'and',
  Or = 'or',
}

export const ExpressionOperatorsLabel: Record<ExpressionOperatorEnum, string> = {
  [ExpressionOperatorEnum.And]: 'NOTIFICATIONS.EXPRESSION_OPERATORS.AND',
  [ExpressionOperatorEnum.Or]: 'NOTIFICATIONS.EXPRESSION_OPERATORS.OR',
}
